import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  downloadEstimateCommAttachment,
  fetchEstimateCommAttachmentBlob,
} from "../../api/Estimates/estimateRfqApi";
import { renderPdfThumbnailFromBlob } from "../../utils/pdfThumbnail";

function formatSize(size) {
  const n = Number(size || 0);
  if (!n) return "";
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

function isImageAtt(filename, mimetype) {
  const mime = String(mimetype || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(filename || ""));
}

function isPdfAtt(filename, mimetype) {
  const mime = String(mimetype || "").toLowerCase();
  if (mime.includes("pdf")) return true;
  return /\.pdf$/i.test(String(filename || ""));
}

function ThumbCard({ children, onClick, title, theme }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      title={title}
      sx={{
        appearance: "none",
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.95),
        bgcolor: alpha(theme.palette.common.white, 0.95),
        borderRadius: 2,
        p: 0.75,
        width: 104,
        cursor: "pointer",
        textAlign: "left",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        },
      }}
    >
      {children}
    </Box>
  );
}

function AttachmentThumb({ att, commId, theme, registerUrl, onOpen }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(Boolean(commId));

  const image = isImageAtt(att.filename, att.mimetype);
  const pdf = isPdfAtt(att.filename, att.mimetype);
  const sizeLabel = formatSize(att.size);

  useEffect(() => {
    if (!commId || !att?.id) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const blob = await fetchEstimateCommAttachmentBlob(commId, att.id);
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        registerUrl?.(objectUrl);
        setBlobUrl(objectUrl);
        if (image) {
          setThumbUrl(objectUrl);
        } else if (pdf) {
          try {
            const dataUrl = await renderPdfThumbnailFromBlob(blob, 160);
            if (!cancelled) setThumbUrl(dataUrl);
          } catch {
            /* icono PDF de fallback */
          }
        }
      } catch {
        /* sin miniatura */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [commId, att?.id, att?.filename, att?.mimetype, image, pdf, registerUrl]);

  return (
    <ThumbCard
      theme={theme}
      title={att.filename}
      onClick={() =>
        onOpen({
          att,
          blobUrl,
          kind: image ? "image" : pdf ? "pdf" : "file",
        })
      }
    >
      <Box
        sx={{
          width: "100%",
          height: 72,
          borderRadius: 1.25,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.6,
        }}
      >
        {loading ? (
          <CircularProgress size={18} />
        ) : thumbUrl ? (
          <Box
            component="img"
            src={thumbUrl}
            alt={att.filename}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : pdf ? (
          <PictureAsPdfOutlinedIcon
            sx={{ color: "error.main", fontSize: 32 }}
          />
        ) : (
          <InsertDriveFileOutlinedIcon
            sx={{ color: "text.secondary", fontSize: 30 }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        fontWeight={700}
        noWrap
        sx={{ display: "block", lineHeight: 1.25 }}
      >
        {att.filename || "adjunto"}
      </Typography>
      {sizeLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: 10 }}
        >
          {sizeLabel}
        </Typography>
      ) : null}
    </ThumbCard>
  );
}

/**
 * Miniaturas de adjuntos del email RFQ/comunicación.
 * Click → popup con preview (imagen/PDF) o descarga.
 */
export default function EstimateCommAttachmentThumbs({
  attachments,
  commId,
  theme,
  onError,
}) {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const objectUrlsRef = useRef([]);

  const list = useMemo(
    () => (Array.isArray(attachments) ? attachments.filter((a) => a?.id) : []),
    [attachments],
  );

  const registerUrl = useMemo(
    () => (url) => {
      if (url) objectUrlsRef.current.push(url);
    },
    [],
  );

  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* ignore */
        }
      });
      objectUrlsRef.current = [];
    };
  }, []);

  const openPreview = async (base) => {
    if (base.blobUrl) {
      setPreview(base);
      return;
    }
    if (!commId || !base.att?.id) {
      setPreview(base);
      return;
    }
    setPreview({ ...base, blobUrl: null });
    setPreviewLoading(true);
    try {
      const blob = await fetchEstimateCommAttachmentBlob(commId, base.att.id);
      const objectUrl = URL.createObjectURL(blob);
      registerUrl(objectUrl);
      setPreview({ ...base, blobUrl: objectUrl });
    } catch (err) {
      onError?.(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo abrir el adjunto",
      );
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!list.length) return null;

  return (
    <>
      <Box
        sx={{
          px: 1.25,
          py: 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.15),
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ mb: 1 }}
        >
          <AttachFileIcon sx={{ fontSize: 16, color: "primary.main" }} />
          <Typography variant="caption" fontWeight={800}>
            {list.length} adjunto{list.length === 1 ? "" : "s"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          {list.map((att) => (
            <AttachmentThumb
              key={att.id}
              att={att}
              commId={commId}
              theme={theme}
              registerUrl={registerUrl}
              onOpen={openPreview}
            />
          ))}
        </Stack>
      </Box>

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        fullWidth
        maxWidth={preview?.kind === "file" ? "xs" : "md"}
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ pr: 2, pb: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={800}
            component="span"
            noWrap
          >
            {preview?.att?.filename || "Adjunto"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: alpha("#000", 0.02) }}>
          {previewLoading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : !preview ? (
            <Skeleton variant="rectangular" height={240} />
          ) : preview.kind === "image" && preview.blobUrl ? (
            <Box
              component="img"
              src={preview.blobUrl}
              alt={preview.att?.filename || "Imagen"}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "70vh",
                mx: "auto",
                borderRadius: 1,
              }}
            />
          ) : preview.kind === "pdf" && preview.blobUrl ? (
            <Box
              component="iframe"
              src={preview.blobUrl}
              title={preview.att?.filename || "PDF"}
              sx={{
                width: "100%",
                height: "70vh",
                border: "none",
                borderRadius: 1,
                bgcolor: "#fff",
              }}
            />
          ) : (
            <Stack alignItems="center" spacing={1.25} sx={{ py: 3 }}>
              <InsertDriveFileOutlinedIcon
                sx={{ fontSize: 48, color: "text.secondary" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Vista previa no disponible para este tipo de archivo. Puedes
                descargarlo.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setPreview(null)}>Cerrar</Button>
          {commId && preview?.att?.id ? (
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await downloadEstimateCommAttachment(
                    commId,
                    preview.att.id,
                    preview.att.filename,
                  );
                } catch (err) {
                  onError?.(
                    err?.response?.data?.error ||
                      err?.message ||
                      "No se pudo descargar el adjunto",
                  );
                }
              }}
            >
              Descargar
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}
